import React from "react";
import { Box, Flex, Text, Icon, useColorModeValue } from "@chakra-ui/react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IoCompass,
  IoCompassOutline,
  IoHeart,
  IoHeartOutline,
  IoChatbubble,
  IoChatbubbleOutline,
  IoSettings,
  IoSettingsOutline,
} from "react-icons/io5";
import { useMessages } from "../store/messages-context";
import { MOBILE_BOTTOM_NAV_HEIGHT } from "../constants/mobileApp";

/**
 * Fixed bottom tab bar — only visible below the `lg` breakpoint (see `display`).
 * Gives primary app routes a native-app style affordance on phones.
 */
export default function MobileBottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const { totalUnreadCount } = useMessages();

  let hasUser = false;
  try {
    hasUser = !!localStorage.getItem("user");
  } catch {
    hasUser = false;
  }

  const show =
    hasUser &&
    pathname !== "/" &&
    pathname !== "/Account-disabled" &&
    pathname !== "/get-started" &&
    pathname !== "/onboarding" &&
    !pathname.startsWith("/admin");

  const barBg = useColorModeValue("rgba(255,255,255,0.86)", "rgba(14,14,16,0.86)");
  const barBorder = useColorModeValue("rgba(15,23,42,0.08)", "rgba(255,255,255,0.06)");
  const inactive = useColorModeValue("gray.500", "whiteAlpha.500");
  const active = useColorModeValue("brand.600", "brand.300");
  const indicatorColor = useColorModeValue("brand.500", "brand.300");
  const pillShadow = useColorModeValue(
    "0 -10px 30px rgba(15,23,42,0.08)",
    "0 -8px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)"
  );

  const isShop = pathname === "/shop";
  const isLikes = pathname === "/likes" || pathname.startsWith("/like/");
  const isMessages = pathname === "/Messages" || pathname.startsWith("/messages/");
  const isSettings = pathname === "/settings";

  if (!show) return null;

  const Item = ({
    to,
    label,
    iconActive: ActiveIcon,
    iconIdle: IdleIcon,
    active: isActive,
    badgeCount = 0,
  }) => (
    <Link to={to} style={{ flex: 1, textDecoration: "none", minWidth: 0 }}>
      <Flex
        direction="column"
        align="center"
        justify="center"
        position="relative"
        gap={0.5}
        py={1.5}
        color={isActive ? active : inactive}
        transition="color 0.15s ease, transform 0.15s ease"
        _active={{ transform: "scale(0.94)" }}
      >
        <Box
          position="absolute"
          top="2px"
          h="3px"
          w={isActive ? "22px" : "0px"}
          bg={indicatorColor}
          borderRadius="full"
          transition="width 0.2s ease, opacity 0.2s ease"
          opacity={isActive ? 1 : 0}
        />
        <Box position="relative" lineHeight={0} overflow="visible">
          <Icon as={isActive ? ActiveIcon : IdleIcon} boxSize="22px" />
          {badgeCount > 0 && (
            <Flex
              position="absolute"
              top="-6px"
              right="-10px"
              minW="18px"
              h="18px"
              px="5px"
              align="center"
              justify="center"
              borderRadius="full"
              bg="rgba(254, 226, 226, 0.95)"
              color="#b91c1c"
              fontSize="10px"
              fontWeight="bold"
              lineHeight={1}
              borderWidth="1px"
              borderColor="rgba(252, 165, 165, 0.7)"
              boxShadow="0 3px 10px rgba(239, 68, 68, 0.28)"
              pointerEvents="none"
              aria-hidden="true"
              zIndex={2}
            >
              {badgeCount > 99 ? "99+" : badgeCount}
            </Flex>
          )}
        </Box>
        <Text
          fontSize="9px"
          fontWeight={isActive ? "semibold" : "medium"}
          letterSpacing="0.02em"
          lineHeight="1.1"
          noOfLines={1}
        >
          {label}
        </Text>
      </Flex>
    </Link>
  );

  return (
    <Box
      display={{ base: "block", lg: "none" }}
      position="fixed"
      left={0}
      right={0}
      bottom={0}
      zIndex={1100}
      bg={barBg}
      borderTopWidth="1px"
      borderTopColor={barBorder}
      backdropFilter="blur(20px) saturate(180%)"
      sx={{
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxShadow: pillShadow,
      }}
    >
      <Flex px={1} minH={MOBILE_BOTTOM_NAV_HEIGHT} align="stretch">
        <Item
          to="/shop"
          label={t("nav.explore")}
          iconActive={IoCompass}
          iconIdle={IoCompassOutline}
          active={isShop}
        />
        <Item
          to="/likes"
          label={t("nav.likes")}
          iconActive={IoHeart}
          iconIdle={IoHeartOutline}
          active={isLikes}
        />
        <Item
          to="/Messages"
          label={t("nav.chats")}
          iconActive={IoChatbubble}
          iconIdle={IoChatbubbleOutline}
          active={isMessages}
          badgeCount={totalUnreadCount}
        />
        <Item
          to="/settings"
          label={t("nav.settings")}
          iconActive={IoSettings}
          iconIdle={IoSettingsOutline}
          active={isSettings}
        />
      </Flex>
    </Box>
  );
}
